// 3rd party
import React, { ReactElement } from 'react';
import styled from 'styled-components';

// Types
import { InputChangeHandler } from 'types';

// Components
import { InputLabel } from 'components';

// Component properties
interface Props {
  id?: string;
  width?: number; // give as pixel value
  height?: number; // give as pixel value
  checked?: boolean;
  onChange?: InputChangeHandler;
  label?: string;
  labelWeight?: number;
  className?: string;
  labelColor?: string;
  disabled?: boolean;
  fontSize?: number;
  verticalLayout?: boolean;
  name?: string;
}

// Styling
const Root = styled.div<Props>`
  position: relative;
  width: auto;
  height: auto;
  display: flex;
  flex-direction: ${(props) => (props.verticalLayout ? 'column' : 'row')};
  align-items: ${(props) => (props.verticalLayout ? 'flex-start' : 'center')};
  align-self: center;
`;

const Input = styled.input<Props>`
  position: relative;
  width: ${(props) => (props.width ? `${props.width / 16}rem` : '1.2rem')};
  height: ${(props) => (props.height ? `${props.height / 16}rem` : '1.2rem')};
  appearance: none;
  background-color: white;
  border-radius: 50%;
  border: ${(props) => `1px solid ${props.theme.colors.mediumGrey4}`};
  margin: 0;
  order: ${(props) => (props.verticalLayout ? 2 : 0)};

  &[type='radio']:hover {
    background-color: ${(props) => props.theme.colors.primaryBlue5};
    border-color: ${(props) => props.theme.colors.primaryBlue5};
    box-shadow: inset 0 0 0 2.5px white, 0 0 0 1px rgba(0, 118, 204, 0.5);
    -webkit-box-shadow: inset 0 0 0 2.8px white, 0 0 0 1px rgba(0, 118, 204, 0.5); /* Adjusted for Safari */
  }

  &[type='radio']:focus-selected {
    background-color: ${(props) => props.theme.colors.primaryBlue5};
    border-color: ${(props) => props.theme.colors.primaryBlue5};
    box-shadow: inset 0 0 0 2.5px white, 0 0 0 1px rgba(0, 118, 204, 0.5);
    -webkit-box-shadow: inset 0 0 0 2.8px white, 0 0 0 1px rgba(0, 118, 204, 0.5); /* Adjusted for Safari */
    outline: 3px solid #cfe5f6;
  }

  &[type='radio']:checked {
    background-color: ${(props) => props.theme.colors.primaryBlue5};
    border-color: ${(props) => props.theme.colors.primaryBlue5};
    box-shadow: inset 0 0 0 4.2px white, 0 0 0 1px rgba(0, 118, 204, 0.5);
    -webkit-box-shadow: inset 0 0 0 4.4px white, 0 0 0 1px rgba(0, 118, 204, 0.5); /* Adjusted for Safari */
    outline: none;
  }

  &[type='radio']:disabled {
    background-color: ${(props) => props.theme.colors.background.background2};
    border-color: ${(props) => props.theme.colors.tableborder};
    outline: none;
  }

  &[type='radio']:hover:disabled {
    outline: none;
    box-shadow: none;
    -webkit-box-shadow: none;
  }

  &[type='radio']:checked:disabled {
    background-color: ${(props) => props.theme.colors.tableborder};
    border-color: ${(props) => props.theme.colors.tableborder};
    box-shadow: inset 0 0 0 0.26rem #f9fafb;
    -webkit-box-shadow: inset 0 0 0 0.26rem #f9fafb; /* Adjusted for Safari */
    outline: none;
  }
`;

const LabelContainer = styled.div<Props>`
  margin-left: ${(props) => (props.verticalLayout ? '0' : '0.625rem')};
  margin-bottom: ${(props) => (props.verticalLayout ? '0.5rem' : '0')};
  order: ${(props) => (props.verticalLayout ? 1 : 0)};
  & > label {
    margin-bottom: 0;
    font-size: ${(props) => (props.fontSize ? `${props.fontSize / 16}rem` : `0.75rem`)}
    font-weight: ${(props) =>
      props.labelWeight ? `${props.labelWeight} !important` : '400 !important'};
  }
  .radio-label {
    font-size: 14px;
    line-height: 16.8px;
    color  ${(props) => props.theme.colors.text.lightBlack};
    font-weight: ${(props) =>
      props.labelWeight ? `${props.labelWeight} !important` : '400 !important'};
  }
`;

const RadioButton = ({
  id,
  checked,
  onChange,
  width,
  height,
  label,
  labelWeight,
  labelColor,
  className,
  disabled,
  fontSize,
  name,
  verticalLayout
}: Props): ReactElement => (
  <Root className={className} verticalLayout={verticalLayout}>
    <Input
      id={`radio-${id}`}
      type="radio"
      disabled={disabled}
      className="radio-button"
      checked={checked}
      onChange={onChange}
      width={width}
      height={height}
      verticalLayout={verticalLayout}
      name={name}
    ></Input>
    {label && (
      <LabelContainer labelWeight={labelWeight} fontSize={fontSize} verticalLayout={verticalLayout}>
        <InputLabel color={labelColor}>
          <label htmlFor={`radio-${id}`} className="radio-label">
            {label}
          </label>
        </InputLabel>
      </LabelContainer>
    )}
  </Root>
);

export default RadioButton;
